import { trackEvent, trackEngagement } from './analytics';

// Chat widget analytics constants
export const CHAT_EVENTS = {
  OPEN: 'Open',
  CLOSE: 'Close',
  SEND_MESSAGE: 'SendMessage',
  RECEIVE_MESSAGE: 'ReceiveMessage',
  START_FORM: 'StartContactForm',
  CANCEL_FORM: 'CancelContactForm',
  SUBMIT_FORM: 'SubmitContactForm',
  ERROR: 'Error',
  COMMAND_USED: 'CommandUsed',
  FORM_FIELD_COMPLETE: 'FormFieldComplete',
  VALIDATION_ERROR: 'ValidationError'
};

// Import the ContactFormData type from chatUtils
import { ContactFormData } from '../components/chat/utils/chatUtils';

/**
 * Track when the chat widget is opened
 */
export const trackChatOpen = (sessionId: string, isMobile: boolean) => {
  trackEvent('ChatWidget', CHAT_EVENTS.OPEN, 'Chat widget opened', undefined, {
    device_type: isMobile ? 'mobile' : 'desktop',
    session_id: sessionId,
    page_url: window.location.href,
    page_title: document.title
  });
};

/**
 * Track when the chat widget is closed
 */
export const trackChatClose = (sessionId: string, isMobile: boolean, messageCount: number, duration: number) => {
  trackEvent('ChatWidget', CHAT_EVENTS.CLOSE, 'Chat widget closed', duration, {
    device_type: isMobile ? 'mobile' : 'desktop',
    session_id: sessionId,
    message_count: messageCount,
    had_interaction: messageCount > 1,
    page_url: window.location.href
  });
};

/**
 * Track user messages sent in the chat
 */
export const trackUserMessage = (
  sessionId: string, 
  messageLength: number, 
  isContactFormMode: boolean, 
  contactFormStep: string | null
) => {
  trackEvent('ChatWidget', CHAT_EVENTS.SEND_MESSAGE, 'User sent message', undefined, {
    session_id: sessionId,
    message_length: messageLength,
    in_contact_form: isContactFormMode,
    contact_form_step: contactFormStep,
    page_url: window.location.href
  });
  
  // Track engagement for content analysis
  trackEngagement(
    sessionId,
    'chat_message',
    'sent',
    undefined
  );
};

/**
 * Track bot messages received in the chat
 */
export const trackBotMessage = (
  sessionId: string, 
  messageLength: number, 
  isContactFormMode: boolean, 
  contactFormStep: string | null
) => {
  trackEvent('ChatWidget', CHAT_EVENTS.RECEIVE_MESSAGE, 'Bot sent message', undefined, {
    session_id: sessionId,
    message_length: messageLength,
    in_contact_form: isContactFormMode,
    contact_form_step: contactFormStep,
    page_url: window.location.href
  });
  
  // Track engagement for content analysis
  trackEngagement(
    sessionId,
    'chat_message',
    'received',
    undefined
  );
};

/**
 * Track when the contact form is started
 */
export const trackContactFormStart = (sessionId: string, isMobile: boolean) => {
  trackEvent('ChatWidget', CHAT_EVENTS.START_FORM, 'Contact form started', undefined, {
    session_id: sessionId,
    device_type: isMobile ? 'mobile' : 'desktop',
    page_url: window.location.href
  });
};

/**
 * Track when the contact form is cancelled
 */
export const trackContactFormCancel = (
  sessionId: string, 
  lastStep: string | null, 
  fieldsCompleted: number
) => {
  trackEvent('ChatWidget', CHAT_EVENTS.CANCEL_FORM, 'Contact form cancelled', undefined, {
    session_id: sessionId,
    last_step: lastStep,
    fields_completed: fieldsCompleted,
    page_url: window.location.href
  });
};

/**
 * Track when a form field is completed
 */
export const trackFormFieldComplete = (
  sessionId: string, 
  field: string, 
  valueLength?: number
) => {
  trackEvent('ChatWidget', CHAT_EVENTS.FORM_FIELD_COMPLETE, `${field} field completed`, undefined, {
    session_id: sessionId,
    field,
    value_length: valueLength,
    page_url: window.location.href
  });
};

/**
 * Track when a validation error occurs
 */
export const trackValidationError = (
  sessionId: string, 
  field: string
) => {
  trackEvent('ChatWidget', CHAT_EVENTS.VALIDATION_ERROR, `${field} validation failed`, undefined, {
    session_id: sessionId,
    field,
    page_url: window.location.href
  });
};

/**
 * Track when the contact form is submitted
 */
export const trackContactFormSubmit = (
  sessionId: string, 
  formData: ContactFormData
) => {
  trackEvent('ChatWidget', CHAT_EVENTS.SUBMIT_FORM, 'Contact form submitted', undefined, {
    session_id: sessionId,
    has_name: !!formData.name,
    has_email: !!formData.email,
    has_subject: !!formData.subject,
    message_length: formData.message?.length || 0,
    page_url: window.location.href
  });
};

/**
 * Track when a command is used in the chat
 */
export const trackCommandUsed = (
  sessionId: string, 
  command: string, 
  message?: string
) => {
  trackEvent('ChatWidget', CHAT_EVENTS.COMMAND_USED, `${command} command used`, undefined, {
    session_id: sessionId,
    command,
    message,
    page_url: window.location.href
  });
};

/**
 * Track errors in the chat widget
 */
export const trackChatError = (
  sessionId: string, 
  errorType: string, 
  errorMessage: string
) => {
  trackEvent('ChatWidget', CHAT_EVENTS.ERROR, errorType, undefined, {
    session_id: sessionId,
    error_message: errorMessage,
    page_url: window.location.href
  });
};

/**
 * Generate a dashboard report URL for the chat widget analytics
 */
export const getChatAnalyticsDashboardUrl = (sessionId?: string) => {
  const baseUrl = 'https://www.periciatecnica.eng.br/admin/analytics/chat';
  return sessionId ? `${baseUrl}?session=${sessionId}` : baseUrl;
};

/**
 * Export all chat analytics functions in a single object for easier imports
 */
export const chatAnalytics = {
  trackChatOpen,
  trackChatClose,
  trackUserMessage,
  trackBotMessage,
  trackContactFormStart,
  trackContactFormCancel,
  trackFormFieldComplete,
  trackValidationError,
  trackContactFormSubmit,
  trackCommandUsed,
  trackChatError,
  getChatAnalyticsDashboardUrl,
  CHAT_EVENTS
}; 